import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';

// Supported languages
export const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'pt', 'de', 'ru'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Language display names
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  ru: 'Русский',
};

// Language flag emojis
export const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  es: '🇪🇸',
  pt: '🇧🇷',
  de: '🇩🇪',
  ru: '🇷🇺',
};

/**
 * Detect user's preferred language from:
 * 1. localStorage (user preference)
 * 2. navigator.language (browser preference)
 * 3. Default to English
 */
function detectLanguage(): SupportedLanguage {
  // Check localStorage first
  const storedLanguage = localStorage.getItem('language');
  if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage as SupportedLanguage)) {
    return storedLanguage as SupportedLanguage;
  }

  // Check browser language preference
  const browserLang = navigator.language.split('-')[0]; // Get language code (e.g., 'en' from 'en-US')
  if (SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)) {
    return browserLang as SupportedLanguage;
  }

  // Default to English
  return 'en';
}

/**
 * Initialize i18next with language detection and HTTP backend
 */
export async function initI18n(): Promise<void> {
  const detectedLanguage = detectLanguage();

  await i18next.use(HttpBackend).init({
    lng: detectedLanguage,
    fallbackLng: 'en',
    ns: ['ui', 'notifications', 'scenes'],
    defaultNS: 'ui',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    interpolation: {
      escapeValue: false, // Phaser doesn't require XSS protection
    },
  });
}

/**
 * Change language and save preference to localStorage
 */
export function changeLanguage(language: SupportedLanguage): Promise<any> {
  localStorage.setItem('language', language);
  return i18next.changeLanguage(language);
}

/**
 * Get current language
 */
export function getCurrentLanguage(): SupportedLanguage {
  return i18next.language as SupportedLanguage;
}

export default i18next;
